/**
 * Sign-in page.
 *
 * Google OAuth through Supabase is the primary path. A local-development session
 * button appears only when the deployment explicitly enables it, and it is
 * refused by the Worker in production.
 */

import { useEffect, useState } from 'react';
import { Alert } from '../components/ui';
import { configProblems, webConfig } from '../lib/config';
import { useRouter } from '../lib/router';
import { useSession } from '../lib/session';

export function SignInPage() {
  const { status, signInWithGoogle, signInWithDevSession } = useSession();
  const { navigate, query } = useRouter();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const nextPath = query.get('next') ?? '/app';

  useEffect(() => {
    if (status === 'signed_in') navigate(nextPath, { replace: true });
  }, [status, navigate, nextPath]);

  const problems = configProblems();

  return (
    <div className="container-narrow">
      <div className="page-head">
        <h1>Sign in</h1>
        <p>
          A Google account is required. Every diagnosis is owned by your account, and the API
          refuses unauthenticated requests outright.
        </p>
      </div>

      <div className="card">
        {problems.length > 0 ? (
          <Alert tone="warning" title="This deployment is not fully configured">
            <ul className="plain-list">
              {problems.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
            <p className="small">
              Until VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY are set, Google sign-in cannot
              complete. See docs/google-oauth-setup.md.
            </p>
          </Alert>
        ) : null}

        {error ? (
          <Alert tone="error" title="Sign-in failed">
            {error}
          </Alert>
        ) : null}

        <div className="stack">
          <button
            type="button"
            className="button button--primary"
            disabled={busy || !webConfig.supabaseConfigured}
            onClick={async () => {
              setError(null);
              setBusy(true);
              try {
                await signInWithGoogle(nextPath);
              } catch (caught) {
                setError(
                  caught instanceof Error ? caught.message : 'Google sign-in could not be started.',
                );
                setBusy(false);
              }
            }}
          >
            Continue with Google
          </button>

          {webConfig.devAuthEnabled ? (
            <div>
              <hr />
              <p className="small faint">
                Local development mode is enabled on this deployment. This button asks the
                self-hosted Worker for a short-lived local session and is refused when the Worker is
                not running with DEV_AUTH_ENABLED=true outside production.
              </p>
              <button
                type="button"
                className="button"
                disabled={busy}
                onClick={async () => {
                  setError(null);
                  setBusy(true);
                  try {
                    await signInWithDevSession();
                    navigate(nextPath, { replace: true });
                  } catch (caught) {
                    setError(
                      caught instanceof Error
                        ? caught.message
                        : 'The local development session could not be created.',
                    );
                    setBusy(false);
                  }
                }}
              >
                Start a local development session
              </button>
            </div>
          ) : null}

          <p className="small faint">
            By signing in you agree to the <a href="/terms">terms of use</a> and the{' '}
            <a href="/privacy">privacy notes</a> for this deployment.
          </p>
        </div>
      </div>
    </div>
  );
}
